import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const STATUS_DOT = {
  Pass: '#1E9331',
  Failed: '#C7362F',
  Skipped: '#9A9DA0',
};

const Rail = styled.div`
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;

  @media (max-width: 900px) {
    display: flex;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const RailTitle = styled.div`
  color: #8a8d90;
  font-size: 11px;
  font-family: Gotham, sans-serif;
  font-weight: 400;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 12px 14px 8px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: none;
  border-left: 2px solid ${props => (props.$selected ? '#1788C5' : 'transparent')};
  background: ${props => (props.$selected ? '#f6f8fa' : 'transparent')};
  cursor: pointer;
  transition: background 0.12s ease;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f1f3;
  }

  &:hover {
    background: ${props => (props.$selected ? '#f6f8fa' : '#fafbfc')};
  }

  @media (max-width: 900px) {
    width: auto;
    flex-shrink: 0;
    border-bottom: none;
    border-left: none;
    border-right: 1px solid #f0f1f3;
    border-bottom: 2px solid ${props => (props.$selected ? '#1788C5' : 'transparent')};
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  flex-shrink: 0;
  background: ${props => props.$color};
`;

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
`;

const AttemptLabel = styled.span`
  color: ${props => (props.$selected ? '#1a1a1a' : '#3f4145')};
  font-size: 13px;
  font-family: Gotham, sans-serif;
  font-weight: ${props => (props.$selected ? 400 : 350)};
  line-height: 18px;
  white-space: nowrap;
`;

const ItemMeta = styled.span`
  color: #8a8d90;
  font-size: 11.5px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
  line-height: 16px;
  white-space: nowrap;
`;

function countChecks(attempt) {
  const list = attempt.checksList || [];
  const passed = list.filter(c => c.status === 'passed').length;
  return { passed, total: list.length };
}

export default function AttemptTimeline({ attempts, selectedId, onSelect }) {
  return (
    <Rail>
      <RailTitle>Attempts ({attempts.length})</RailTitle>
      {attempts.map((attempt, idx) => {
        const selected = attempt.id === selectedId;
        const { passed, total } = countChecks(attempt);
        const attemptNumber = typeof attempt.id === 'number' ? attempt.id : attempts.length - idx;
        return (
          <Item
            key={attempt.id}
            type="button"
            $selected={selected}
            onClick={() => onSelect(attempt.id)}
          >
            <Dot $color={STATUS_DOT[attempt.state] || STATUS_DOT.Failed} />
            <ItemBody>
              <AttemptLabel $selected={selected}>Attempt #{attemptNumber}</AttemptLabel>
              <ItemMeta>
                {total > 0 ? `${passed}/${total} · ` : ''}
                {attempt.timestamp}
              </ItemMeta>
            </ItemBody>
          </Item>
        );
      })}
    </Rail>
  );
}

AttemptTimeline.propTypes = {
  attempts: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AttemptTimeline.defaultProps = {
  selectedId: null,
};
